/**
 * /workspace/sell/orders — live seller orders backed by shop_orders collection.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Package, Truck, CheckCircle2, XCircle, Search, Loader2, IndianRupee, Filter,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "placed", "confirmed", "packed", "shipped", "delivered", "cancelled", "returned"];
const NEXT_ACTION: Record<string, { to: string; label: string; tone: string } | null> = {
  placed:    { to: "confirmed", label: "Confirm", tone: "blue" },
  confirmed: { to: "packed",    label: "Mark packed", tone: "amber" },
  packed:    { to: "shipped",   label: "Ship it", tone: "fuchsia" },
  shipped:   { to: "delivered", label: "Mark delivered", tone: "emerald" },
  delivered: null,
  cancelled: null,
  returned:  null,
};

interface Order {
  order_id: string;
  buyer_name: string;
  buyer_email: string;
  buyer_state: string;
  product_title: string;
  quantity: number;
  total: number;
  tax_gst: number;
  status: string;
  payment_method: string;
  placed_at: string;
  shipped_at?: string | null;
  delivered_at?: string | null;
}

const PAGE = 25;

export default function SellOrders() {
  const [status, setStatus] = React.useState("all");
  const [q, setQ] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const qc = useQueryClient();

  const orders = useQuery<{ items: Order[]; total: number; has_more: boolean; kpi: any }>({
    queryKey: ["sell-orders", status, q, offset],
    queryFn: async () => (await api.get(`/workspace/sell/orders?status=${status}&q=${encodeURIComponent(q)}&limit=${PAGE}&offset=${offset}`)).data,
  });

  const advance = useMutation({
    mutationFn: async ({ order_id, to }: { order_id: string; to: string }) => (await api.post(`/workspace/sell/orders/${order_id}/status`, { status: to })).data,
    onSuccess: () => { toast.success("Order updated."); qc.invalidateQueries({ queryKey: ["sell-orders"] }); },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Update failed"),
  });

  const k = orders.data?.kpi;

  return (
    <DashboardLayout title="Orders" subtitle="Every marketplace order across your workspace — filter, search and advance status.">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="sell-orders-kpi">
        <Kpi label="Total orders" value={k?.orders ?? "—"} icon={Package} tone="blue"/>
        <Kpi label="GMV" value={inr(k?.gmv)} icon={IndianRupee} tone="emerald"/>
        <Kpi label="Taxable value" value={inr(k?.taxable)} icon={IndianRupee} tone="fuchsia"/>
        <Kpi label="GST collected" value={inr(k?.gst)} icon={IndianRupee} tone="amber"/>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
        {STATUSES.map((s) => (
          <button key={s}
            data-testid={`sell-orders-filter-${s}`}
            onClick={() => { setStatus(s); setOffset(0); }}
            className={`h-8 px-3 rounded-full text-xs font-semibold capitalize border transition-colors ${
              status === s
                ? "bg-[color:var(--sb-navy,#0A1128)] text-white border-transparent"
                : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"
            }`}
          >
            {s === "all" ? "All" : s}
            {k?.by_status?.[s] ? <span className="ml-1 text-[10px] opacity-70">·{k.by_status[s]}</span> : null}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
          <Input
            data-testid="sell-orders-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
            placeholder="Search buyer, product, order ID…"
            className="h-8 w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden">
        {orders.isLoading ? (
          <div className="p-8 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/> Loading…</div>
        ) : (orders.data?.items?.length ?? 0) === 0 ? (
          <div className="p-12 text-center text-[color:var(--sg-muted)]">No orders match this filter.</div>
        ) : (
          <div className="overflow-x-auto sg-scrollbar-thin">
            <table className="w-full text-sm" data-testid="sell-orders-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                  <th className="text-left py-2 pl-4">Order · Placed</th>
                  <th className="text-left py-2">Buyer</th>
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2">Qty</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.data!.items.map((o) => {
                  const next = NEXT_ACTION[o.status];
                  return (
                    <tr key={o.order_id} className="border-b border-[color:var(--sg-border)]/60 hover:bg-[color:var(--sg-muted-bg)]/40" data-testid={`sell-order-row-${o.order_id}`}>
                      <td className="py-2 pl-4">
                        <div className="font-mono text-[11px] text-[color:var(--sg-fg)]">{o.order_id}</div>
                        <div className="text-[10px] text-[color:var(--sg-muted)]">{new Date(o.placed_at).toLocaleString()}</div>
                      </td>
                      <td className="py-2">
                        <div className="font-medium text-[color:var(--sg-fg)]">{o.buyer_name}</div>
                        <div className="text-[10px] text-[color:var(--sg-muted)]">{o.buyer_email} · {o.buyer_state}</div>
                      </td>
                      <td className="py-2 max-w-[280px]">
                        <div className="truncate text-[color:var(--sg-fg)]">{o.product_title}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{o.quantity}</td>
                      <td className="py-2 text-right tabular-nums font-semibold text-[color:var(--sg-fg)]">{inr(o.total)}</td>
                      <td className="py-2"><StatusBadge status={o.status}/></td>
                      <td className="py-2 pr-4 text-right">
                        {next && (
                          <Button
                            data-testid={`sell-order-advance-${o.order_id}`}
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px]"
                            disabled={advance.isPending && advance.variables?.order_id === o.order_id}
                            onClick={() => advance.mutate({ order_id: o.order_id, to: next.to })}
                          >
                            {advance.isPending && advance.variables?.order_id === o.order_id
                              ? <Loader2 className="h-3 w-3 animate-spin"/>
                              : next.label}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {orders.data && orders.data.total > PAGE && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-[color:var(--sg-border)] text-xs text-[color:var(--sg-muted)]">
            <div>Showing {offset + 1}-{Math.min(offset + PAGE, orders.data.total)} of {orders.data.total.toLocaleString()}</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 px-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}><ChevronLeft className="h-3.5 w-3.5"/></Button>
              <Button variant="outline" size="sm" className="h-7 px-2" disabled={!orders.data.has_more} onClick={() => setOffset(offset + PAGE)}><ChevronRight className="h-3.5 w-3.5"/></Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function inr(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function Kpi({ label, value, icon: Icon, tone }: { label: string; value: any; icon: any; tone: string }) {
  const t: Record<string, string> = {
    blue:    "text-blue-600 dark:text-blue-300 from-blue-500/15",
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300 from-fuchsia-500/15",
    amber:   "text-amber-600 dark:text-amber-300 from-amber-500/15",
  };
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${t[tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${t[tone].split(" ")[0]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {label}
        </div>
        <div className="mt-1.5 font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    placed:    "bg-slate-100 text-slate-700 dark:bg-slate-800/40 dark:text-slate-200",
    confirmed: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200",
    packed:    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    shipped:   "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200",
    delivered: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    cancelled: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
    returned:  "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status] || map.placed}`}>{status}</span>;
}
